document.addEventListener("alpine:init", () => {
  console.log("Alpine.js initializing cart store");

  Alpine.store("ajCart", {
    // ===== STATE PROPERTIES =====
    cartDiscount: 0,
    cart: {},
    allProducts: [],
    totalComparePrice: 0,
    setLoader: false,
    _fetchDepth: 0,
    upsellProducts: [],
    openCart: false,
    freeProductThreshold: 0,
    freeSampleThreshold: 0,
    freeProductID: null,
    showFreeSamples: null,
    singleFreeSampleID: null,

    // ===== COMPUTED GETTERS =====
    get hasSubscription() {
      const items = this.cart && this.cart.items;
      if (!items || !items.length) return false;
      return items.some((item) => item && item.selling_plan_allocation);
    },

    // ===== CART DRAWER CONTROLS =====
    toggleCart() {
      this.openCart = !this.openCart;
      this.toggleBodyScroll();
    },

    openCartDrawer() {
      this.openCart = true;
      document.body.classList.add("cart-open");
    },

    closeCartDrawer() {
      this.openCart = false;
      document.body.classList.remove("cart-open");
    },

    toggleBodyScroll() {
      if (this.openCart) {
        document.body.classList.add("cart-open");
      } else {
        document.body.classList.remove("cart-open");
      }
    },

    // ===== CART DATA FETCHING =====
    async fetchCart() {
      this._fetchDepth++;
      this.setLoader = true;
      try {
        await this.fetchAllProducts();
        const cartRequest = await fetch("/cart.js");
        const cart = await cartRequest.json();
        this.cart = cart;
        await this.getCartProducts();
        await this.getTotalComparePrice();
        await this.getGiftSampleCount();
        await this.getFreeThresholdGiftCount();
        await this.removeGifts();

        console.log(this.cart);
        console.log(this.freeProductThreshold);

        if (this.singleFreeSampleID) {
          await this.addGiftProduct(this.singleFreeSampleID);
        }
      } catch (error) {
        console.error("Error fetching cart:", error);
      } finally {
        this._fetchDepth--;
        if (this._fetchDepth <= 0) {
          this._fetchDepth = 0;
          this.setLoader = false;
        }
      }
    },

    async fetchAllProducts() {
      if (this.allProducts.length > 0) return;
      try {
        const response = await fetch("/products.json");
        const data = await response.json();
        this.allProducts = data.products;
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    },

    async fetchCollection(handle) {
      try {
        const response = await fetch(`/collections/${handle}/products.json`);
        const data = await response.json();
        return data.products;
      } catch (error) {
        console.error("Error fetching collection:", error);
        return [];
      }
    },

    // ===== CART CALCULATIONS =====
    async getTotalComparePrice() {
      if (!this.cart.items) {
        return;
      }
      let comparePrice = 0;

      // Filter out any undefined items and calculate total
      this.cart.items
        .filter((item) => item)
        .forEach((item) => {
          if (item.compare_at_price && item.compare_at_price > 0) {
            // Product has an explicit compare_at_price (sale item)
            comparePrice += item.compare_at_price * item.quantity;
          } else if (item.original_line_price && item.original_line_price > item.final_line_price) {
            // Subscription discount: original_line_price is higher than final
            comparePrice += item.original_line_price / 100;
          } else {
            comparePrice += (item.price / 100) * item.quantity;
          }
        });

      this.totalComparePrice = comparePrice;
    },

    async getCartProducts() {
      if (!this.cart.items) {
        return;
      }
      // Enrich cart items with compare_at_price and availability from product data
      this.cart.items = this.cart.items.map((item) => {
        const product = this.allProducts.find((p) => p.id === item.product_id);

        if (product) {
          const variant = product.variants.find(
            (v) => v.id === item.variant_id
          );
          if (variant) {
            // Determine compare_at_price:
            // 1. Use explicit compare_at_price if set (sale item)
            // 2. For subscription items: variant.price is the original price,
            //    item.price is the discounted subscription price (both in cents)
            let cap = variant.compare_at_price;
            if (!cap && item.selling_plan_allocation && variant.price > item.price / 100) {
              cap = variant.price;
            }
            return {
              ...item,
              compare_at_price: cap,
              available: variant.available,
            };
          }
        }
        return {
          ...item,
        };
      });
    },

    // ===== CART ITEM MANAGEMENT =====
    async changeQuantity(line, newQuantity, oldQuantity) {
      this.setLoader = true;

      // if (!oldQuantity) {
      //   this.setLoader = false;
      //   return;
      // }

      try {
        const response = await fetch(
          window.Shopify.routes.root + "cart/change.js",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ line: line, quantity: newQuantity }),
          }
        );

        const data = await response.json();

        await this.fetchCart();
        await this.freeThresholdGift();
      } catch (error) {
        console.error("Failed to change quantity from the cart:", error);
      } finally {
        this.setLoader = false;
      }
    },

    async addToCart(productID) {
      try {
        const response = await fetch(
          window.Shopify.routes.root + "cart/add.js",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              items: [
                {
                  id: productID,
                  quantity: 1,
                },
              ],
            }),
          }
        );

        const data = await response.json();
        console.log("Added to cart");
        await this.fetchCart();
        await this.freeThresholdGift();

        this.openCartDrawer();
      } catch (error) {
        console.error("Couldn't add item to cart" + error);
      }
    },

    async removeItem(id) {
      try {
        const response = await fetch(
          window.Shopify.routes.root + "cart/update.js",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              updates: {
                [id]: 0,
              },
            }),
          }
        );

        const data = await response.json();

        this.fetchCart();
        await this.freeThresholdGift();
      } catch (error) {
        console.error("Can't remove item from cart" + error);
      }
    },

    // ===== FREE GIFT & SAMPLE LOGIC =====
    async freeThresholdGift() {
      if (this.cart.total_price / 100 < this.freeProductThreshold) return;

      if (this.getFreeThresholdGiftCount() >= 1) return;

      try {
        const response = await fetch(
          window.Shopify.routes.root + "cart/add.js",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              items: [
                {
                  id: this.freeProductID,
                  quantity: 1,
                  properties: {
                    _freeThresholdGift: "true",
                  },
                },
              ],
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        await this.fetchCart();
      } catch (error) {
        console.error("Couldn't add gift to cart:", error);
        this.setLoader = false;
      }
    },

    async addGiftProduct(productID) {
      let numberOfGifts;

      if (this.showFreeSamples) {
        numberOfGifts = 1;
      } else {
        numberOfGifts = 1;
      }

      if (this.getGiftSampleCount() === numberOfGifts) return;

      if (
        numberOfGifts === 1 &&
        this.cart.total_price / 100 < this.freeSampleThreshold &&
        this.getGiftSampleCount() === 1
      ) {
        try { this.removeItem(this.singleFreeSampleID); } catch (e) { /* ignore */ }
      } else if (this.cart.total_price / 100 > this.freeSampleThreshold) {
        try {
          const response = await fetch(
            window.Shopify.routes.root + "cart/add.js",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                items: [
                  {
                    id: productID,
                    quantity: 1,
                    properties: {
                      _gift: "true",
                    },
                  },
                ],
              }),
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          await this.fetchCart();
        } catch (error) {
          console.error("Couldn't add gift to cart:", error);
        }
      }
    },

    removeGifts() {
      if (this.cart.total_price / 100 < this.freeProductThreshold) {
        const thresholdGift = this.cart.items.filter(
          (item) =>
            item.properties && item.properties._freeThresholdGift === "true"
        );

        if (thresholdGift) {
          thresholdGift.forEach((gift) => this.removeItem(gift.id));
        }
      }

      if (this.cart.total_price / 100 < this.freeSampleThreshold) {
        const gifts = this.cart.items.filter(
          (item) => item.properties && item.properties._gift === "true"
        );

        if (gifts) {
          gifts.forEach((gift) => this.removeItem(gift.id));
        }
      }
    },

    // ===== HELPER FUNCTIONS =====
    getGiftSampleCount(_property) {
      if (!this.cart.items) return 0;
      // if(_property === )
      return this.cart.items.filter((item) => item.properties?._gift).length;
    },

    giftInCart(id) {
      if (!this.cart.items) return false;

      const item = this.cart.items.some(
        (item) =>
          item.id == id && item.properties && item.properties._gift === "true"
      );

      return item;
    },

    getFreeThresholdGiftCount() {
      if (!this.cart.items) return 0;
      return this.cart.items.filter(
        (item) => item.properties?._freeThresholdGift
      ).length;
    },

    // ===== UPSELL PRODUCT LOGIC =====
    async getUpsellProducts(id) {
      await this.fetchAllProducts();

      const matchingProduct = this.allProducts.filter(
        (product) => product.id === id
      );

      if (matchingProduct.length && matchingProduct[0].tags) {
        let nextStep = 0;
        if (matchingProduct[0].tags.includes("step-1")) {
          nextStep = "step-2";
        }
        if (matchingProduct[0].tags.includes("step-2")) {
          nextStep = "step-3";
        }
        if (matchingProduct[0].tags.includes("step-3")) {
          nextStep = "step-4";
        }
        if (matchingProduct[0].tags.includes("step-4")) {
          nextStep = "step-5";
        }
        if (nextStep) {
          this.upsellProducts = await this.fetchCollection(nextStep);
        } else {
          this.upsellProducts = await this.fetchCollection("bestsellers");
        }
      } else {
        this.upsellProducts = await this.fetchCollection("bestsellers");
      }
    },

    // ===== INITIALIZATION =====
    init() {
      this.fetchCart();
    },
  });
});

// ===== ADD TO CART BUTTON LISTENERS =====
const AddToCartButton = document.querySelectorAll(".t4s-product-form__submit");
const cartAddToCartButton = document.querySelectorAll(
  ".t4s-pr-addtocart[data-action-atc]"
);

if (AddToCartButton) {
  AddToCartButton.forEach((btn) =>
    btn.addEventListener("click", async (e) => {
      // Wait for the native form submission to complete by polling cart count
      const prevCount = Alpine.store("ajCart").cart?.item_count ?? 0;
      const maxWait = 5000;
      const interval = 150;
      let waited = 0;

      const poll = async () => {
        try {
          const res = await fetch("/cart.js");
          const cart = await res.json();
          if (cart.item_count !== prevCount || waited >= maxWait) {
            await Alpine.store("ajCart").fetchCart();
            Alpine.store("ajCart").openCartDrawer();
            Alpine.store("ajCart").freeThresholdGift();
            return;
          }
        } catch (err) { /* ignore */ }
        waited += interval;
        if (waited < maxWait) setTimeout(poll, interval);
      };
      setTimeout(poll, 200);
    })
  );

  if (cartAddToCartButton) {
    cartAddToCartButton.forEach((btn) =>
      btn.addEventListener("click", async (e) => {
        const prevCount = Alpine.store("ajCart").cart?.item_count ?? 0;
        const maxWait = 5000;
        const interval = 150;
        let waited = 0;

        const poll = async () => {
          try {
            const res = await fetch("/cart.js");
            const cart = await res.json();
            if (cart.item_count !== prevCount || waited >= maxWait) {
              await Alpine.store("ajCart").fetchCart();
              Alpine.store("ajCart").openCartDrawer();
              Alpine.store("ajCart").freeThresholdGift();
              return;
            }
          } catch (err) { /* ignore */ }
          waited += interval;
          if (waited < maxWait) setTimeout(poll, interval);
        };
        setTimeout(poll, 200);
      })
    );
  }
}

// ===== STICKY CART BUTTON LISTENER (Event Delegation) =====
document.addEventListener("click", (e) => {
  const stickyAtcButton = e.target.closest(".t4s-sticky-atc__atc[data-action-atc]");
  if (stickyAtcButton) {
    const prevCount = Alpine.store("ajCart").cart?.item_count ?? 0;
    const maxWait = 5000;
    const interval = 150;
    let waited = 0;

    const poll = async () => {
      try {
        const res = await fetch("/cart.js");
        const cart = await res.json();
        if (cart.item_count !== prevCount || waited >= maxWait) {
          await Alpine.store("ajCart").fetchCart();
          Alpine.store("ajCart").openCartDrawer();
          Alpine.store("ajCart").freeThresholdGift();
          return;
        }
      } catch (err) { /* ignore */ }
      waited += interval;
      if (waited < maxWait) setTimeout(poll, interval);
    };
    setTimeout(poll, 200);
  }
});

// ===== STICKY ATC: PASS ALONG SELECTED SUBSCRIPTION (SELLING PLAN) =====
// The sticky add-to-cart button posts to /cart/add.js with only {id, quantity},
// so a selling plan chosen in the Firmhouse widget on the main form never rode
// along. We capture the currently selected selling_plan before the theme's own
// delegated click handler fires, then inject it into the very next cart/add
// request body.
(() => {
  let pendingSellingPlan = null;

  document.addEventListener(
    "click",
    (e) => {
      if (!e.target.closest(".t4s-sticky-atc__atc[data-action-atc]")) return;
      const input = document.getElementById("pcd-fh-selling-plan-input");
      pendingSellingPlan = input && input.value ? input.value : null;
    },
    true
  );

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    if (pendingSellingPlan && init && typeof init.body === "string") {
      try {
        const data = JSON.parse(init.body);
        if (Array.isArray(data.items)) {
          data.items.forEach((item) => {
            if (item && item.id && !item.selling_plan) {
              item.selling_plan = pendingSellingPlan;
            }
          });
          init = Object.assign({}, init, { body: JSON.stringify(data) });
        }
      } catch (err) {
        /* not a JSON cart/add body, leave untouched */
      }
      pendingSellingPlan = null;
    }
    return originalFetch(input, init);
  };
})();

// ===== STICKY CART VISIBILITY =====
// Keep the sticky cart visible by default and only hide it when the
// main product add-to-cart button is currently inside the viewport.
(() => {
  const FORCE_VISIBLE_CLASS = "aj-sticky-atc-force-visible";
  const FORCE_HIDDEN_CLASS = "aj-sticky-atc-force-hidden";
  const PRIMARY_ATC_SELECTOR = ".t4s-product-form__submit";
  const STICKY_ATC_SELECTOR = ".t4s-sticky-atc[data-sticky-addtocart]";

  let syncFrame = null;

  function isRenderable(element) {
    if (!element || element.hidden) {
      return false;
    }

    const styles = window.getComputedStyle(element);
    return styles.display !== "none" && styles.visibility !== "hidden";
  }

  function isInViewport(element) {
    if (!isRenderable(element)) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;

    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < viewportHeight &&
      rect.left < viewportWidth
    );
  }

  function getStickyAtc() {
    return document.querySelector(STICKY_ATC_SELECTOR);
  }

  function getPrimaryAddToCartButton() {
    const buttons = Array.from(document.querySelectorAll(PRIMARY_ATC_SELECTOR));

    return (
      buttons.find(
        (button) =>
          !button.closest(".t4s-sticky-atc") &&
          !button.closest("template") &&
          isRenderable(button)
      ) || null
    );
  }

  function syncStickyAtcVisibility() {
    const stickyAtc = getStickyAtc();

    if (!stickyAtc || stickyAtc.hidden) {
      return;
    }

    const primaryAddToCartButton = getPrimaryAddToCartButton();
    const shouldHideStickyAtc = primaryAddToCartButton
      ? isInViewport(primaryAddToCartButton)
      : false;

    stickyAtc.classList.toggle(FORCE_HIDDEN_CLASS, shouldHideStickyAtc);
    stickyAtc.classList.toggle(FORCE_VISIBLE_CLASS, !shouldHideStickyAtc);
  }

  function queueStickyAtcSync() {
    if (syncFrame) {
      return;
    }

    syncFrame = window.requestAnimationFrame(() => {
      syncFrame = null;
      syncStickyAtcVisibility();
    });
  }

  function initStickyAtcVisibilityController() {
    queueStickyAtcSync();

    window.addEventListener("scroll", queueStickyAtcSync, { passive: true });
    window.addEventListener("resize", queueStickyAtcSync);
    document.addEventListener("variant:changed", queueStickyAtcSync);

    if ("MutationObserver" in window && document.body) {
      const observer = new MutationObserver(queueStickyAtcSync);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    window.setTimeout(queueStickyAtcSync, 250);
    window.setTimeout(queueStickyAtcSync, 1000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initStickyAtcVisibilityController, {
      once: true,
    });
  } else {
    initStickyAtcVisibilityController();
  }
})();

